'use client';

import type { MouseEvent, ReactNode } from 'react';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { SocialIcon } from '../../icons/SocialIcon';
import { pickBlockField } from '../../lib/localized';
import { pickChrome, type SiteChromeKey } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import type { ReservationChannelAction } from './buildDirectContactActions';

export interface ReservationChannelShellProps extends BlockRenderProps {
  /** Section voice — `direct` = WhatsApp and/or phone; `third-party` = external booker. */
  channel: 'third-party' | 'direct';
  actions: ReservationChannelAction[];
  nextChromeKey: SiteChromeKey;
}

/**
 * Channel reservation shell — copy column + one or more CTAs (External single,
 * or direct-contact WhatsApp / phone).
 */
export function ReservationChannelShell({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  channel,
  actions,
  nextChromeKey,
}: ReservationChannelShellProps) {
  const target = isActive ? activeEditTarget : null;
  const title = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const note = pickBlockField(block.content, 'note', locale);
  /** Direct Call-Us — no next-step line (chrome helper removed; not editable). */
  const nextStep =
    channel === 'direct'
      ? ''
      : pickBlockField(block.content, 'nextStep', locale) || pickChrome(nextChromeKey, locale);
  const showNext = channel !== 'direct' && (Boolean(nextStep) || Boolean(editable));

  const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (editable) {
      event.preventDefault();
    }
  };

  const hasMessenger = actions.some((action) => action.voice === 'messenger');
  const hasCall = actions.some((action) => action.voice === 'call');
  const visibleActions = actions.filter(
    (action) => Boolean(action.label) || Boolean(editable),
  );

  return (
    <section
      className={[
        'ws-reservation',
        'ws-reservation--channel',
        channel === 'direct' ? 'ws-reservation--direct' : 'ws-reservation--third-party',
        hasMessenger ? 'ws-reservation--messenger' : '',
        hasCall ? 'ws-reservation--call' : '',
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-reservation-channel={channel}
      id="reserve"
    >
      <div className="ws-reservation__inner">
        {title || editable ? (
          <EditableContent
            as="h2"
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-reservation__title"
          >
            {title}
          </EditableContent>
        ) : null}
        <div className="ws-reservation__copy">
          <EditableContent
            fieldKey="supporting"
            editable={editable}
            activeEditTarget={target}
            className="ws-reservation__subtitle"
          >
            {supporting}
          </EditableContent>
          {note || editable ? (
            <EditableContent
              fieldKey="note"
              editable={editable}
              activeEditTarget={target}
              className="ws-reservation__note"
            >
              {note}
            </EditableContent>
          ) : null}
        </div>
        <div className="ws-reservation__action">
          {visibleActions.length > 0 ? (
            <div
              className="ws-reservation__cta-stack"
              data-cta-count={visibleActions.length}
            >
              {visibleActions.map((action) => {
                const markSize = channel === 'direct' ? 32 : 20;
                const mark: ReactNode = action.socialIcon ? (
                  <SocialIcon
                    icon={action.socialIcon}
                    size={markSize}
                    className="ws-reservation__cta-mark"
                  />
                ) : (
                  <LucideIcon
                    icon={action.icon}
                    size={markSize}
                    strokeWidth={channel === 'direct' ? 1 : 1.5}
                    className="ws-reservation__cta-mark"
                  />
                );
                return (
                  <a
                    key={action.voice}
                    className="ws-reservation__cta"
                    data-reservation-cta={action.voice}
                    href={editable ? '#' : action.href}
                    onClick={handleCtaClick}
                    {...(action.openInNewTab && !editable
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {action.iconFieldKey ? (
                      <EditableContent
                        as="span"
                        fieldKey={action.iconFieldKey}
                        editable={editable}
                        activeEditTarget={target}
                        className="ws-reservation__cta-icon"
                      >
                        {mark}
                      </EditableContent>
                    ) : (
                      <span className="ws-reservation__cta-icon">{mark}</span>
                    )}
                    <EditableContent
                      as="span"
                      fieldKey={action.labelFieldKey}
                      editable={editable}
                      activeEditTarget={target}
                      className="ws-reservation__cta-label"
                    >
                      {action.label}
                    </EditableContent>
                  </a>
                );
              })}
            </div>
          ) : null}
          {showNext ? (
            <EditableContent
              as="p"
              fieldKey="nextStep"
              editable={editable}
              activeEditTarget={target}
              className="ws-reservation__next"
              data-reservation-next={channel}
            >
              {nextStep}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
